import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';

import { ROUTE_SCAN_SELF_SERVICE } from '@/common/constants';
import {
  ChildrenDefaultProps,
  ChildrenPropTypes,
} from '@/common/proptypes/Base/Children.proptypes';
import getScanSelfServiceProduct from '@/ebat/api/scan-self-service/getScanSelfServiceProduct';
import useScanSelfServiceProduct from '@/ebat/queries/scanSelfService/useScanSelfServiceProduct';

const getQueryKey = (uid, codeOrRef) => [
  `${uid}/products/${codeOrRef}`,
  codeOrRef,
];

const ScanSelfServiceContext = createContext(null);

const ScanSelfServiceProvider = ({ children }) => {
  const { uid, codeOrRef } = useParams();
  const queryClient = useQueryClient();
  const history = useHistory();
  const queryKey = getQueryKey(uid, codeOrRef);
  const [error, setError] = useState();
  const [pending, setPending] = useState(false);

  const prevFetchErrorRef = useRef(null);
  const {
    data: product,
    isLoading,
    error: fetchError,
  } = useScanSelfServiceProduct(queryKey, codeOrRef === undefined);
  const setProduct = (nextProduct) => {
    const nextCodeOrRef = nextProduct.sku;
    queryClient.setQueryData(getQueryKey(uid, nextCodeOrRef), nextProduct);
    history.push(`${ROUTE_SCAN_SELF_SERVICE}/${uid}/${nextCodeOrRef}`);
  };

  const searchProduct = (nextCodeOrRef) => {
    setPending(true);
    getScanSelfServiceProduct({ queryKey: getQueryKey(uid, nextCodeOrRef) })
      .then((data) => {
        setPending(false);
        setProduct(data);
      })
      .catch((reason) => {
        setPending(false);
        setError(reason);
      });
  };
  const clearSearchError = () => {
    setError(undefined);
  };
  const clearProduct = () => {
    history.push(`${ROUTE_SCAN_SELF_SERVICE}/${uid}`);
    clearSearchError();
  };

  useEffect(() => {
    if (!prevFetchErrorRef.current && fetchError) {
      history.push(`${ROUTE_SCAN_SELF_SERVICE}/${uid}`);
    }
    prevFetchErrorRef.current = fetchError;
  }, [fetchError, history, uid]);

  return (
    <ScanSelfServiceContext.Provider
      value={{
        product,
        setProduct,
        searchProduct,
        clearProduct,
        isLoading: isLoading || pending,
        searchPending: pending,
        error,
        clearSearchError,
        hasProduct: !!product,
      }}
    >
      {children}
    </ScanSelfServiceContext.Provider>
  );
};

ScanSelfServiceProvider.defaultProps = {
  ...ChildrenDefaultProps,
};

ScanSelfServiceProvider.propTypes = {
  ...ChildrenPropTypes,
};

export const useScanSelfServiceContext = () =>
  useContext(ScanSelfServiceContext);

export default ScanSelfServiceProvider;
